// useContextMenuClose.js
import { useEffect, useRef, useCallback } from "react";

/**
 * Закриває контекстне меню при:
 * - кліку поза меню (mousedown)
 * - натисканні Escape (keydown)
 * - (опц.) скролі документа
 * - (опц.) ресайзі вікна
 * - (опц.) blur за межі меню
 * - (опц.) автозакритті через N мс (з паузою на hover)
 */
export function useContextMenuClose({
    ref,               // React ref на контейнер меню
    open,              // boolean: чи відкрите меню
    onClose,           // () => void: що робити при закритті
    closeOnScroll = false,
    closeOnResize = false,
    closeOnBlur = false,
    autoCloseMs,       // number | undefined
    pauseOnHover = false,
}) {
    const timerRef = useRef(null);
    const hoveredRef = useRef(false);

    const clearTimer = useCallback(() => {
        if (timerRef.current) {
            clearTimeout(timerRef.current);
            timerRef.current = null;
        }
    }, []);

    // ★ 1) Фокусимо меню, щойно відкрили (щоб дублювати обробку через onKeyDown)
    useEffect(() => {
        if (!open) return;
        const el = ref?.current;
        if (el && typeof el.focus === "function") {
            // невелика затримка, щоб елемент гарантовано в DOM
            requestAnimationFrame(() => el.focus());
        }
    }, [open, ref]);

    // Клік поза меню / Esc / scroll / resize
    useEffect(() => {
        if (!open) return;

        const isEscape = (e) =>
            e.key === "Escape" || e.code === "Escape" || e.keyCode === 27;

        const handleClickOutside = (e) => {
            const el = ref?.current;
            if (!el) return;
            if (!el.contains(e.target)) onClose();
        };

        const handleKeyDown = (e) => {
            if (isEscape(e)) onClose();
        };

        const handleScroll = () => {
            if (closeOnScroll) onClose();
        };

        const handleResize = () => {
            if (closeOnResize) onClose();
        };

        document.addEventListener("mousedown", handleClickOutside);
        // ★ 2) Слухаємо keydown у capture-фазі, щоб нас не «перебили»
        document.addEventListener("keydown", handleKeyDown, true);
        if (closeOnScroll) {
            document.addEventListener("scroll", handleScroll, { passive: true, capture: true });
        }
        if (closeOnResize) {
            window.addEventListener("resize", handleResize);
        }

        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
            document.removeEventListener("keydown", handleKeyDown, true); // ★ 3) зняти теж у capture
            if (closeOnScroll) {
                document.removeEventListener("scroll", handleScroll, { capture: true });
            }
            if (closeOnResize) {
                window.removeEventListener("resize", handleResize);
            }
        };
    }, [open, onClose, ref, closeOnScroll, closeOnResize]);

    // Автозакриття (з паузою на hover, якщо треба)
    useEffect(() => {
        clearTimer();
        if (!open || !autoCloseMs || autoCloseMs <= 0) return;

        const tick = () => {
            // якщо пауза на hover вмикнена — не закриваємося поки курсор над меню
            if (pauseOnHover && hoveredRef.current) {
                timerRef.current = setTimeout(tick, 250);
                return;
            }
            onClose();
        };

        timerRef.current = setTimeout(tick, autoCloseMs);
        return clearTimer;
    }, [open, autoCloseMs, pauseOnHover, onClose, clearTimer]);

    // Пропси для контейнера меню
    const menuProps = {
        tabIndex: -1,
        onKeyDown: (e) => {
            if (e.key === "Escape") onClose();
        },
        onContextMenu: (e) => e.preventDefault(),
        onBlur: closeOnBlur
            ? (e) => {
                const el = ref?.current;
                const next = e.relatedTarget;
                if (!el) return onClose();
                if (!next || !el.contains(next)) onClose();
            }
            : undefined,
        // Пауза автозакриття під час hover
        onMouseEnter: pauseOnHover ? () => { hoveredRef.current = true; } : undefined,
        onMouseLeave: pauseOnHover ? () => { hoveredRef.current = false; } : undefined,
    };

    return { menuProps };
}
